import { cn } from "@/lib/utils";
import { INTERACTIVE_FOCUS } from "../../lib/chrome";
import { useLandingCopy } from "../../lib/copy";
import { LANDING_CONTENT_MEASURE_CLASS } from "../../lib/layout";
import { useLandingLinks } from "../../lib/links";
import { CtaPortrait } from "./cta-portrait";
import "./testimonial.css";

const CTA_LINK_CLASS =
	"inline-flex h-[46px] w-[134px] items-center justify-center px-6 text-sm font-normal leading-[1.3] tracking-[-0.02em] whitespace-nowrap no-underline max-md:h-11 max-md:w-[128px]";

/** Shared inset for the left column — keeps the testimonial rule off the card edges. */
const CTA_CONTENT_PADDING_CLASS = "px-5 md:px-[52px]";

/** Figma S5 — equal top padding on copy + symmetric gap above/below the testimonial rule. */
const CTA_SECTION_PADDING_TOP_CLASS = "pt-8 md:pt-10 lg:pt-10";
const CTA_SECTION_PADDING_BOTTOM_CLASS = "pb-8 md:pb-10 lg:pb-10";
const CTA_SEPARATOR_PADDING_CLASS = "pt-8 md:pt-10 lg:pt-10";

const TESTIMONIAL_PORTRAIT_CLASS = "select-none";

/** Figma S5 — white CTA card: copy + portrait + inset quote row. */
export function SectionFourCtaCard() {
	const { getInTouchHref, getStartedHref } = useLandingLinks();
	const { cta, testimonial } = useLandingCopy();

	return (
		<div
			className={cn(
				LANDING_CONTENT_MEASURE_CLASS,
				"grid grid-cols-1 bg-white lg:grid-cols-[minmax(0,1fr)_360px] lg:items-stretch",
			)}
			data-landing-section-four-cta-card=""
		>
			<div className="order-2 flex min-w-0 flex-col lg:order-none lg:col-start-1">
				<div
					className={cn(
						CTA_CONTENT_PADDING_CLASS,
						CTA_SECTION_PADDING_TOP_CLASS,
						CTA_SECTION_PADDING_BOTTOM_CLASS,
						"flex min-w-0 flex-col",
					)}
				>
					<h2 className="m-0 max-w-[596px] text-landing-section font-normal text-teal-500">{cta.heading}</h2>
					<p className="m-0 mt-1 max-w-[512px] text-landing-section-body font-light text-teal-500">{cta.body}</p>
					<div className="mt-7 flex flex-wrap items-center gap-3">
						<a
							className={cn(
								CTA_LINK_CLASS,
								"bg-teal-500 text-white hover:bg-teal-400 focus-visible:bg-teal-400",
								INTERACTIVE_FOCUS,
							)}
							href={getInTouchHref}
						>
							{cta.getInTouchLabel}
						</a>
						<a
							className={cn(
								CTA_LINK_CLASS,
								"bg-green-500 text-teal-500 hover:bg-green-400 focus-visible:bg-green-400",
								INTERACTIVE_FOCUS,
							)}
							href={getStartedHref}
						>
							{cta.getStartedLabel}
						</a>
					</div>
				</div>

				<figure
					className={cn(
						CTA_CONTENT_PADDING_CLASS,
						CTA_SECTION_PADDING_BOTTOM_CLASS,
						"m-0 flex w-full min-w-0 flex-col",
					)}
				>
					<div
						className={cn(
							"flex w-full min-w-0 flex-col items-start gap-6 border-t border-greige-400 lg:flex-row lg:items-center lg:justify-start lg:gap-10",
							CTA_SEPARATOR_PADDING_CLASS,
						)}
					>
						<blockquote className="m-0 min-w-0 w-full flex-1 text-left text-sm font-normal leading-[1.3] tracking-[-0.02em] text-teal-500 lg:order-2 lg:max-w-[min(560px,100%)]">
							{testimonial.quote}
						</blockquote>
						<figcaption className="flex w-auto shrink-0 flex-row items-center gap-10 text-left lg:order-1 lg:gap-7">
							{testimonial.href ? (
								<a
									aria-label={`${testimonial.authorName}, ${testimonial.authorRole}`}
									className={cn("inline-flex shrink-0 no-underline", INTERACTIVE_FOCUS)}
									href={testimonial.href}
								>
									<img
										alt=""
										className={TESTIMONIAL_PORTRAIT_CLASS}
										data-landing-testimonial-portrait
										src={testimonial.portraitSrc}
									/>
								</a>
							) : (
								<img
									alt={testimonial.portraitAlt}
									className={TESTIMONIAL_PORTRAIT_CLASS}
									data-landing-testimonial-portrait
									src={testimonial.portraitSrc}
								/>
							)}
							<span className="flex min-w-0 flex-col items-start gap-1 lg:gap-1.5">
								{testimonial.brandLogoSrc ? (
									<img
										alt={testimonial.brandLogoAlt ?? ""}
										className="h-4 w-auto shrink-0 object-contain lg:h-5"
										src={testimonial.brandLogoSrc}
									/>
								) : null}
								<span className="text-sm font-medium leading-[1.3] tracking-[-0.02em] text-teal-500 lg:text-[18px]">
									{testimonial.authorName}
								</span>
								<span className="text-landing-caption font-normal leading-[1.3] tracking-[-0.02em] text-grey-1000">
									{testimonial.authorRole}
								</span>
							</span>
						</figcaption>
					</div>
				</figure>
			</div>

			<CtaPortrait className="order-1 lg:order-none lg:col-start-2 lg:row-start-1" />
		</div>
	);
}
